import { useAppSelector } from "../../hook/store";
import { ESliceMenu } from "../../store/slices/menuSlice";
import { formatThaiCalendarDate } from '../../utilities/bangkokDateTime';
import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ListIcon from '@mui/icons-material/List';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Groups2Icon from '@mui/icons-material/Groups2';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import NavigationBar from '../../components/global/Layout/MainLayout/NavigationBar';
import { ActionPaper } from '../../components/global/Widget';
import { useNavigate } from 'react-router-dom';
import {
	useEnsureBillingRounds,
	useCreateBillingRound,
	useDeleteBillingRound,
	useGetBillingPeriods,
	useGetCustomersBillingSummary,
	useGetPeriodRounds,
} from '../../hook/query/useBilling';
import {
	IBillingCustomerSummary,
	IBillingPeriod,
	IPeriodRound,
	ICreateBillingRoundDto,
	IRegenerateRoundsResponse,
} from '../../compiler/billing.interface';

const formatCurrency = (amount: number) =>
	amount.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});

const formatThaiDate = (d: string) => {
	return formatThaiCalendarDate(d);
};

const EMPTY_CREATE_FORM: ICreateBillingRoundDto = { start_date: '', end_date: '', customer_code: '', notes: '' };

type TemplateType = 'weekly' | 'semi_monthly' | 'monthly' | 'other';

const detectTemplateType = (
	startDate: string,
	endDate: string,
): { type: TemplateType; label: string; color: 'primary' | 'secondary' | 'success' | 'warning' } => {
	const days = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
	if (days <= 7) return { type: 'weekly', label: 'รายสัปดาห์', color: 'primary' };
	if (days <= 16) return { type: 'semi_monthly', label: 'ราย 15 วัน', color: 'secondary' };
	if (days <= 35) return { type: 'monthly', label: 'รายเดือน', color: 'success' };
	return { type: 'other', label: `${days} วัน`, color: 'warning' };
};

const formatBillingCycleLabel = (
	round: IPeriodRound,
): { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' | 'default' } => {
	const snapshot = round.billing_cycle_snapshot;
	if (round.billing_cycle_type === 'weekly') {
		const dayLabels: Record<number, string> = {
			0: 'อา.',
			1: 'จ.',
			2: 'อ.',
			3: 'พ.',
			4: 'พฤ.',
			5: 'ศ.',
			6: 'ส.',
			7: 'อา.',
		};
		const weekStart = snapshot?.week_start_day ?? 1;
		return { label: `รายสัปดาห์ ${dayLabels[weekStart] ?? 'จ.'}`, color: 'primary' };
	}
	if (round.billing_cycle_type === 'semi_monthly') {
		return { label: `ครึ่งเดือน ตัด ${snapshot?.month_split_day ?? 15}`, color: 'secondary' };
	}
	if (round.billing_cycle_type === 'monthly') {
		return { label: `รายเดือน วันที่ ${snapshot?.month_billing_day ?? 1}`, color: 'success' };
	}
	if (round.billing_cycle_type === 'custom') {
		return { label: 'กำหนดเอง', color: 'warning' };
	}
	const fallback = detectTemplateType(round.start_date, round.end_date);
	return { label: fallback.label, color: fallback.color };
};

// ─────────────────────────────────────────────
// PeriodRow — one expandable table row per period, lazy-loads customer detail
// ─────────────────────────────────────────────

interface PeriodRowProps {
	period: IBillingPeriod;
	onNavigate: (period: IBillingPeriod, r?: IPeriodRound) => void;
	onDeleteRound: (r: IPeriodRound) => void;
  canDelete?: boolean;
	searchHighlight?: string;
}

const PeriodRow: React.FC<PeriodRowProps> = ({ period, onNavigate, onDeleteRound, searchHighlight, canDelete }) => {
	const [open, setOpen] = useState(false);
	const [showOnlyOutstanding, setShowOnlyOutstanding] = useState(false);

	// Lazy fetch customer rounds only when expanded
	const { data: roundsData, isLoading: roundsLoading } = useGetPeriodRounds(
		period.start_date,
		period.end_date,
		open,
	);

	// Filter + sort: customer_code ASC
	const customerRounds: IPeriodRound[] = useMemo(() => {
		let rounds = roundsData?.data?.rounds?.filter(r => r.round_type === 'per_customer') ?? [];
		if (showOnlyOutstanding) {
			rounds = rounds.filter(r => r.outstanding_amount > 0);
		}
		return [...rounds].sort((a, b) => {
			return (a.customer_code ?? '').localeCompare(b.customer_code ?? '');
		});
	}, [roundsData, showOnlyOutstanding]);

	// Auto-expand if search matches a customer in this period
	const hasSearchMatch = useMemo(() => {
		if (!searchHighlight) return false;
		const q = searchHighlight.toUpperCase();
		const allRounds = roundsData?.data?.rounds?.filter(r => r.round_type === 'per_customer') ?? [];
		return allRounds.some(r =>
			r.customer_code?.toUpperCase().includes(q) ||
			r.customer_name?.toUpperCase().includes(q)
		);
	}, [roundsData, searchHighlight]);

	const { label, color } = detectTemplateType(period.start_date, period.end_date);
	const hasPerCustomer = period.customer_count > 0;

	return (
		<>
			{/* ── Main period row ── */}
			<TableRow hover sx={{ '& > *': { borderBottom: hasPerCustomer && open ? 'unset' : undefined } }}>
				{/* Expand/collapse button */}
				<TableCell padding="checkbox" sx={{ pl: 1, width: 40 }}>
					{hasPerCustomer && (
						<IconButton size="small" onClick={() => setOpen(!open)}>
							{open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
						</IconButton>
					)}
				</TableCell>

				{/* ประเภทรอบ */}
				<TableCell sx={{ py: 1, width: 115 }}>
					<Chip label={label} size="small" color={color} variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
				</TableCell>

				{/* ช่วงวันที่ */}
				<TableCell sx={{ py: 1 }}>
<Typography className="font-16" fontWeight={700}>
						{formatThaiDate(period.start_date)} – {formatThaiDate(period.end_date)}
					</Typography>

				</TableCell>

				{/* จำนวนลูกค้า */}
				<TableCell sx={{ py: 1, minWidth: 120 }}>
					<Box display="flex" alignItems="center" gap={0.5}>
						<PeopleOutlineIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: '1rem' }} />
						<Typography className="font-16" fontWeight={600}>{period.customer_count.toLocaleString()} ราย</Typography>
					</Box>
					{period.global_round_count > 0 && !hasPerCustomer && (
						<Typography variant="caption" color="text.secondary">รอบรวม</Typography>
					)}
				</TableCell>

				{/* จำนวนใบกำกับภาษี */}
				<TableCell align="center" sx={{ py: 1 }}>
					<Typography className="font-16" fontWeight={600}>{(period.invoice_count ?? 0).toLocaleString()}</Typography>
				</TableCell>

				{/* ยอดรวม */}
				<TableCell align="right" sx={{ py: 1, whiteSpace: 'nowrap' }}>
					<Typography className="font-16" fontWeight={600}>
						{formatCurrency(period.total_amount)}
					</Typography>
				</TableCell>

				{/* ค้างชำระ */}
				<TableCell align="right" sx={{ py: 1, whiteSpace: 'nowrap' }}>
					<Typography
						className="font-16"
						fontWeight={700}
						color={period.outstanding_amount > 0 ? 'error.main' : 'success.main'}
					>
						{formatCurrency(period.outstanding_amount)}
					</Typography>
				</TableCell>

				{/* สถานะ */}
				<TableCell sx={{ py: 1 }}>
					<Stack direction="row" spacing={0.5} flexWrap="wrap">
						{period.pending_count > 0 && (
							<Chip
								label={`รอเก็บ ${period.pending_count} ราย`}
								size="small"
								color="warning"
								variant="filled"
								sx={{ fontSize: '0.68rem', fontWeight: 700 }}
							/>
						)}
						{period.completed_count > 0 && (
							<Chip
								label={`เก็บแล้ว ${period.completed_count} ราย`}
								size="small"
								color="success"
								variant="filled"
								sx={{ fontSize: '0.68rem', fontWeight: 700 }}
							/>
						)}
						{period.pending_count === 0 && period.completed_count === 0 && (
							<Chip label="ยังไม่มีข้อมูล" size="small" variant="outlined" sx={{ fontSize: '0.65rem', color: 'text.disabled' }} />
						)}
					</Stack>
				</TableCell>

				{/* เครื่องมือ */}
				<TableCell align="center" sx={{ py: 1, whiteSpace: 'nowrap', width: 120 }}>
					<Button
						size="small"
						variant="contained"
						startIcon={<VisibilityIcon sx={{ fontSize: '0.8rem !important' }} />}
						onClick={() => onNavigate(period)}
						sx={{ fontSize: 11, py: 0.4, px: 1.2 }}
					>
						รายการ
					</Button>
				</TableCell>
			</TableRow>

			{/* ── Expandable customer-list row (lazy) ── */}
			{hasPerCustomer && (
				<TableRow>
					<TableCell
						colSpan={9}
						sx={{ py: 0, px: 0, bgcolor: 'grey.50', borderBottom: open ? '2px solid' : 'none', borderColor: 'divider' }}
					>
						<Collapse in={open || hasSearchMatch} timeout="auto" unmountOnExit>
							<Box sx={{ px: 3, py: 1.5 }}>
								<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
									<Typography
										variant="caption"
										fontWeight={700}
										color="text.secondary"
										sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
									>
										รายการลูกค้าเฉพาะรอบ ({period.customer_count} ราย)
									</Typography>
									<FormControlLabel
										control={
											<Switch
												size="small"
												checked={showOnlyOutstanding}
												onChange={(e) => setShowOnlyOutstanding(e.target.checked)}
											/>
										}
										label={<Typography fontSize={11} color="text.secondary">แสดงเฉพาะที่ค้าง</Typography>}
										sx={{ mr: 0 }}
									/>
								</Stack>
								{roundsLoading ? (
									<Box display="flex" justifyContent="center" py={2}>
										<CircularProgress size={24} />
									</Box>
								) : (
									<Table size="small">
										<TableHead>
											<TableRow sx={{ bgcolor: '#EFF5F0' }}>
												<TableCell className="font-16" sx={{ fontWeight: 700, py: 0.8, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ลูกค้า</TableCell>
												<TableCell className="font-16" sx={{ fontWeight: 700, py: 0.8, width: 145, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>รอบบิล</TableCell>
												<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 0.8, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>จำนวนบิล</TableCell>
												<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 0.8, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ยอดรวม (บาท)</TableCell>
												<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 0.8, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ค้างชำระ (บาท)</TableCell>
												<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 0.8, width: 110, borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>เครื่องมือ</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{customerRounds.map((r) => {
												const isHighlighted = searchHighlight && (
													r.customer_code?.toUpperCase().includes(searchHighlight.toUpperCase()) ||
													r.customer_name?.toUpperCase().includes(searchHighlight.toUpperCase())
												);
												const billingCycle = formatBillingCycleLabel(r);
												return (
												<TableRow key={r.id} hover sx={isHighlighted ? { bgcolor: 'warning.light' } : undefined}>
													<TableCell sx={{ py: 0.6 }}>
														<Chip label={r.customer_code} size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem', fontWeight: 600 }} />
														{r.customer_name && (
															<Typography fontSize={11} color="text.secondary" sx={{ mt: 0.2 }}>{r.customer_name}</Typography>
														)}
													</TableCell>
													<TableCell sx={{ py: 0.6 }}>
														<Chip
															label={billingCycle.label}
															size="small"
															color={billingCycle.color}
															variant="outlined"
															sx={{ fontSize: '0.65rem', fontWeight: 600 }}
														/>
													</TableCell>
													<TableCell align="right" className="font-16" sx={{ py: 0.6 }}>
														{r.bill_count.toLocaleString()}
													</TableCell>
													<TableCell align="right" className="font-16" sx={{ py: 0.6 }}>
														{formatCurrency(r.total_amount)}
													</TableCell>
													<TableCell
														align="right"
														className="font-16"
														sx={{ py: 0.6, color: r.outstanding_amount > 0 ? 'error.main' : 'inherit', fontWeight: r.outstanding_amount > 0 ? 600 : 400 }}
													>
														{formatCurrency(r.outstanding_amount)}
													</TableCell>
													<TableCell align="center" sx={{ py: 0.6 }}>
														<Stack direction="row" spacing={0.5} justifyContent="center">
															<Button size="small" variant="outlined" color="info" onClick={() => onNavigate(period, r)} sx={{ fontSize: 11, py: 0.2, px: 1 }}>
																ดู
															</Button>
															{canDelete && r.payment_count === 0 && (
																<Button size="small" variant="outlined" color="error" onClick={() => onDeleteRound(r)} sx={{ fontSize: 11, py: 0.2, px: 1 }}>
																	ลบ
																</Button>
															)}
														</Stack>
													</TableCell>
												</TableRow>
												);
											})}
										</TableBody>
									</Table>
								)}
							</Box>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};

const BillingPage: React.FC = () => {
	const navigate = useNavigate();

	// --- Tab state ---
	const [activeTab, setActiveTab] = useState(0);

	// --- Dialog state ---
	const [manageOpen, setManageOpen] = useState(false);
	const [deleteConfirmRound, setDeleteConfirmRound] = useState<IPeriodRound | null>(null);

	// --- Filter state ---
	const [searchCustomer, setSearchCustomer] = useState('');
	const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
	const [yearFilter, setYearFilter] = useState<number>(dayjs().year());
	const [monthFilter, setMonthFilter] = useState<number>(0); // 0 = ทุกเดือน

	// --- Customer tab filter state ---
	const [customerSearch, setCustomerSearch] = useState('');
	const [customerStatusFilter, setCustomerStatusFilter] = useState<'all' | 'has_outstanding' | 'completed'>('all');
	const [customerPage, setCustomerPage] = useState(0);
	const customerRowsPerPage = 25;

	// --- Pagination ---
	const [page, setPage] = useState(0);
	const rowsPerPage = 20;

	// --- Hooks ---
	const ensureMutation = useEnsureBillingRounds();
	const permission = useAppSelector(state => state[ESliceMenu.menu]);
	const deleteMutation = useDeleteBillingRound();

	const { data: periodsResponse, isLoading } = useGetBillingPeriods({
		year: String(yearFilter + 543), // ส่งเป็น พ.ศ.
		month: monthFilter,             // 0 = ทุกเดือน
		status: statusFilter === 'all' ? undefined : statusFilter,
	});
	const allPeriods: IBillingPeriod[] = periodsResponse?.data?.periods ?? [];


	// --- Customer tab data (lazy — only when tab 1) ---
	const { data: customersSummaryResponse, isLoading: customersLoading } = useGetCustomersBillingSummary({
		year: String(yearFilter + 543),
		month: monthFilter,
		search: customerSearch || undefined,
		status_filter: customerStatusFilter,
	});
	const allCustomers: IBillingCustomerSummary[] = customersSummaryResponse?.data?.customers ?? [];
	const pagedCustomers = allCustomers.slice(customerPage * customerRowsPerPage, (customerPage + 1) * customerRowsPerPage);

	// --- Client-side search filter (by period_label or customer count indicator) ---
	const filteredPeriods: IBillingPeriod[] = useMemo(() => {
		const q = searchCustomer.trim().toUpperCase();
		if (!q) return allPeriods;
		return allPeriods.filter((p) =>
			p.period_label.toUpperCase().includes(q),
		);
	}, [allPeriods, searchCustomer]);

	// --- Filtered period summary (computed from filteredPeriods) ---
	const filteredPeriodSummary = useMemo(() => ({
		count: filteredPeriods.length,
		invoice_count: filteredPeriods.reduce((s, p) => s + (p.invoice_count ?? 0), 0),
		total_amount: filteredPeriods.reduce((s, p) => s + p.total_amount, 0),
		outstanding_amount: filteredPeriods.reduce((s, p) => s + p.outstanding_amount, 0),
		pending_count: filteredPeriods.reduce((s, p) => s + p.pending_count, 0),
		completed_count: filteredPeriods.reduce((s, p) => s + p.completed_count, 0),
	}), [filteredPeriods]);

	// --- Filtered customer summary (computed from allCustomers — already server-filtered) ---
	const filteredCustomerSummary = useMemo(() => ({
		count: allCustomers.length,
		total_rounds: allCustomers.reduce((s, c) => s + c.total_rounds, 0),
		pending_rounds: allCustomers.reduce((s, c) => s + c.pending_rounds, 0),
		invoice_count: allCustomers.reduce((s, c) => s + (c.invoice_count ?? 0), 0),
		total_amount: allCustomers.reduce((s, c) => s + c.total_amount, 0),
		paid_amount: allCustomers.reduce((s, c) => s + c.paid_amount, 0),
		outstanding_amount: allCustomers.reduce((s, c) => s + c.outstanding_amount, 0),
	}), [allCustomers]);

	// --- Paginated slice ---
	const pagedPeriods = filteredPeriods.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

	// --- Year options (CE, displayed as BE) ---
	const currentYear = dayjs().year();
	const PROJECT_START_YEAR = 2024; // CE = พ.ศ. 2568 (เริ่มโปรเจค ก.ย. 2568)
	const yearOptions = Array.from({ length: currentYear - PROJECT_START_YEAR + 1 }, (_, i) => PROJECT_START_YEAR + i);

	// --- Navigation helpers ---
	const navigateToPeriod = (period: IBillingPeriod, r?: IPeriodRound) => {
		if (r) {
			const params = new URLSearchParams({
				round: r.round_label,
				start_date: period.start_date,
				end_date: period.end_date,
				round_type: 'per_customer',
				customer_code: r.customer_code ?? '',
			});
			navigate(`/sales/billing-invoices?${params.toString()}`);
		} else {
			const params = new URLSearchParams({
				round: period.period_label,
				start_date: period.start_date,
				end_date: period.end_date,
				round_type: 'global',
			});
			navigate(`/sales/billing-invoices?${params.toString()}`);
		}
	};

	const handleReset = () => {
		setSearchCustomer('');
		setStatusFilter('all');
		setYearFilter(dayjs().year());
		setMonthFilter(0);
		setPage(0);
		setCustomerSearch('');
		setCustomerStatusFilter('all');
		setCustomerPage(0);
	};

	return (
		<>
			<NavigationBar title="รายการวางบิล" />

			<Paper component="section" aria-label="ตัวกรองรายการวางบิล" variant="outlined"
				sx={{ p: 2, mb: 2, minWidth: 0, overflow: 'visible' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
					<Typography fontWeight={600}>ค้นหาและกรองรายการ</Typography>
					<Button
						variant="outlined"
						startIcon={<SettingsIcon />}
						disabled={!permission.create}
						onClick={() => setManageOpen(true)}
						sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
						>
						จัดการรอบบิล
					</Button>
				</Box>
				<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
					{activeTab === 0 ? (
						<>
							<TextField
								size="small"
								placeholder="ค้นหาช่วงวันที่..."
								inputProps={{ 'aria-label': 'ค้นหาช่วงวันที่' }}
								value={searchCustomer}
								onChange={(e) => { setSearchCustomer(e.target.value); setPage(0); }}
								InputProps={{
									startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
								}}
								sx={{ flex: '2 1 240px', minWidth: 0 }}
							/>
							<FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 0 }}>
								<InputLabel>สถานะ</InputLabel>
								<Select value={statusFilter} label="สถานะ" onChange={(e) => { setStatusFilter(e.target.value as 'all' | 'pending' | 'completed'); setPage(0); }}>
									<MenuItem value="all">ทั้งหมด</MenuItem>
									<MenuItem value="pending">รอดำเนินการ</MenuItem>
									<MenuItem value="completed">สำเร็จ</MenuItem>
								</Select>
							</FormControl>
						</>
					) : (
						<>
							<TextField
								size="small"
								placeholder="ค้นหารหัส / ชื่อลูกค้า..."
								inputProps={{ 'aria-label': 'ค้นหารหัสหรือชื่อลูกค้า' }}
								value={customerSearch}
								onChange={(e) => { setCustomerSearch(e.target.value); setCustomerPage(0); }}
								InputProps={{
									startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
								}}
								sx={{ flex: '2 1 240px', minWidth: 0 }}
							/>
							<FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 0 }}>
								<InputLabel>สถานะลูกค้า</InputLabel>
								<Select value={customerStatusFilter} label="สถานะลูกค้า" onChange={(e) => { setCustomerStatusFilter(e.target.value as 'all' | 'has_outstanding' | 'completed'); setCustomerPage(0); }}>
									<MenuItem value="all">ทั้งหมด</MenuItem>
									<MenuItem value="has_outstanding">มียอดค้าง</MenuItem>
									<MenuItem value="completed">ชำระครบ</MenuItem>
								</Select>
							</FormControl>
						</>
					)}
					<FormControl size="small" sx={{ flex: '1 1 104px', minWidth: 0 }}>
						<InputLabel>ปี</InputLabel>
						<Select value={yearFilter} label="ปี" onChange={(e) => { setYearFilter(e.target.value as number); setPage(0); setCustomerPage(0); }}>
							{yearOptions.map((y) => <MenuItem key={y} value={y}>{y + 543}</MenuItem>)}
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 0 }}>
						<InputLabel>เดือน</InputLabel>
						<Select value={monthFilter} label="เดือน" onChange={(e) => { setMonthFilter(Number(e.target.value)); setPage(0); setCustomerPage(0); }}>
							<MenuItem value={0}>ทั้งหมด</MenuItem>
							{[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <MenuItem key={m} value={m}>เดือน {m}</MenuItem>)}
						</Select>
					</FormControl>
					<Tooltip title="รีเซ็ตตัวกรอง">
						<IconButton aria-label="รีเซ็ตตัวกรอง" size="small" onClick={handleReset} sx={{ border: '1px solid', borderColor: 'divider', width: 40, height: 40, boxSizing: 'border-box', flexShrink: 0 }}>
							<RefreshIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Paper>

			{/* ── Summary stat cards ── */}
			<Box sx={{ pb: 2 }}>
				<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 2 }}>
					{activeTab === 0 ? (
						<>
							{/* Card 1: ช่วงวางบิล */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(25,118,210,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<DateRangeIcon sx={{ fontSize: 24, color: 'primary.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>ช่วงวางบิล</Typography>
										<Typography variant="h4" color="primary.main" fontWeight={600}>
											{isLoading ? '—' : filteredPeriodSummary.count.toLocaleString()}
										</Typography>
										<Stack direction="row" spacing={1.5} mt={0.5}>
											<Typography variant="caption" color="warning.main" fontWeight={600}>รอ {filteredPeriodSummary.pending_count.toLocaleString()} ราย</Typography>
											<Typography variant="caption" color="success.main" fontWeight={600}>เก็บแล้ว {filteredPeriodSummary.completed_count.toLocaleString()} ราย</Typography>
										</Stack>
									</Box>
								</Stack>
							</Box>
							{/* Card 2: ใบกำกับภาษี */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(46,125,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<ReceiptLongIcon sx={{ fontSize: 24, color: 'success.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>ใบกำกับภาษี</Typography>
										<Typography variant="h4" color="success.main" fontWeight={600}>
											{isLoading ? '—' : filteredPeriodSummary.invoice_count.toLocaleString()}
										</Typography>
										<Typography variant="caption" color="text.disabled">ใบ</Typography>
									</Box>
								</Stack>
							</Box>
							{/* Card 3: จำนวนเงิน */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: filteredPeriodSummary.outstanding_amount > 0 ? 'rgba(198,40,40,0.08)' : 'rgba(46,125,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<PaidIcon sx={{ fontSize: 24, color: filteredPeriodSummary.outstanding_amount > 0 ? 'error.main' : 'success.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={1}>จำนวนเงิน</Typography>
										<Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
											<Box>
												<Typography variant="caption" color="text.disabled" display="block">ชำระแล้ว</Typography>
												<Typography variant="h5" color={filteredPeriodSummary.outstanding_amount === filteredPeriodSummary.total_amount ? 'text.disabled' : 'success.main'} fontWeight={600} lineHeight={1.2}>
													{isLoading ? '—' : formatCurrency(filteredPeriodSummary.total_amount - filteredPeriodSummary.outstanding_amount)}
												</Typography>
											</Box>
											<Typography color="text.disabled" mb={0.2}>/</Typography>
											<Box>
												<Typography variant="caption" color="text.disabled" display="block">ยอดรวม</Typography>
												<Typography variant="h5" color="text.primary" fontWeight={500} lineHeight={1.2}>
													{isLoading ? '—' : formatCurrency(filteredPeriodSummary.total_amount)}
												</Typography>
											</Box>
										</Stack>
										{!isLoading && filteredPeriodSummary.outstanding_amount > 0 && (
											<Typography variant="caption" color="error.main" fontWeight={500} mt={0.5} display="block">
												ค้างชำระ {formatCurrency(filteredPeriodSummary.outstanding_amount)} บาท
											</Typography>
										)}
									</Box>
								</Stack>
							</Box>
						</>
					) : (
						<>
							{/* Card 1: จำนวนลูกค้า */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(25,118,210,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<Groups2Icon sx={{ fontSize: 24, color: 'primary.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>จำนวนลูกค้า</Typography>
										<Typography variant="h4" color="primary.main" fontWeight={600}>
											{customersLoading ? '—' : filteredCustomerSummary.count.toLocaleString()}
										</Typography>
									</Box>
								</Stack>
							</Box>
							{/* Card 2: ใบกำกับภาษี */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(46,125,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<ReceiptLongIcon sx={{ fontSize: 24, color: 'success.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={0.5}>ใบกำกับภาษี</Typography>
										<Typography variant="h4" color="success.main" fontWeight={600}>
											{customersLoading ? '—' : filteredCustomerSummary.invoice_count.toLocaleString()}
										</Typography>
										<Typography variant="caption" color="text.disabled">ใบ</Typography>
									</Box>
								</Stack>
							</Box>
							{/* Card 3: จำนวนเงิน */}
							<Box sx={{ minWidth: 0, p: { xs: 2, lg: 3 }, borderRadius: 3, bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: filteredCustomerSummary.outstanding_amount > 0 ? 'rgba(198,40,40,0.08)' : 'rgba(46,125,50,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
										<PaidIcon sx={{ fontSize: 24, color: filteredCustomerSummary.outstanding_amount > 0 ? 'warning.main' : 'success.main' }} />
									</Box>
									<Box flex={1}>
										<Typography variant="body2" color="text.secondary" fontWeight={500} mb={1}>จำนวนเงิน</Typography>
										<Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
											<Box>
												<Typography variant="caption" color="text.disabled" display="block">ชำระแล้ว</Typography>
												<Typography variant="h5" color={filteredCustomerSummary.paid_amount === 0 ? 'text.disabled' : 'success.main'} fontWeight={600} lineHeight={1.2}>
													{customersLoading ? '—' : formatCurrency(filteredCustomerSummary.paid_amount)}
												</Typography>
											</Box>
											<Typography color="text.disabled" mb={0.2}>/</Typography>
											<Box>
												<Typography variant="caption" color="text.disabled" display="block">ยอดรวม</Typography>
												<Typography variant="h5" color="text.primary" fontWeight={500} lineHeight={1.2}>
													{customersLoading ? '—' : formatCurrency(filteredCustomerSummary.total_amount)}
												</Typography>
											</Box>
										</Stack>
										{!customersLoading && filteredCustomerSummary.outstanding_amount > 0 && (
											<Typography variant="caption" color="error.main" fontWeight={500} mt={0.5} display="block">
												ค้างชำระ {formatCurrency(filteredCustomerSummary.outstanding_amount)} บาท
											</Typography>
										)}
									</Box>
								</Stack>
							</Box>
						</>
					)}
				</Box>
			</Box>

			{/* ── Tabs ── */}
			<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
				<Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="มุมมองรายการวางบิล">
					<Tab label="มุมมองรอบบิล" sx={{ fontWeight: 600 }} />
					<Tab label="มุมมองลูกค้า" sx={{ fontWeight: 600 }} />
				</Tabs>
			</Box>

			{/* ── Tab 0: Period View ── */}
			{activeTab === 0 && (
			<ActionPaper>
				<Box sx={{ p: 2 }}>
				{/* ── Filtered Period Summary Bar ── */}
				{!isLoading && filteredPeriods.length > 0 && (
					<Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
						<Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
							<Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
								สรุปที่แสดง ({filteredPeriodSummary.count} รอบ)
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Box>
								<Typography fontSize={10} color="text.secondary">รอเก็บ</Typography>
								<Typography className="font-16" fontWeight={700} color="warning.main">{filteredPeriodSummary.pending_count.toLocaleString()} ราย</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">เก็บแล้ว</Typography>
								<Typography className="font-16" fontWeight={700} color="success.main">{filteredPeriodSummary.completed_count.toLocaleString()} ราย</Typography>
							</Box>
							<Divider orientation="vertical" flexItem />
							<Box>
								<Typography fontSize={10} color="text.secondary">ใบกำกับภาษี</Typography>
								<Typography className="font-16" fontWeight={700}>{filteredPeriodSummary.invoice_count.toLocaleString()} ใบ</Typography>
							</Box>
							<Divider orientation="vertical" flexItem />
							<Box>
								<Typography fontSize={10} color="text.secondary">ยอดรวม</Typography>
								<Typography className="font-16" fontWeight={700} color="primary.main">{formatCurrency(filteredPeriodSummary.total_amount)} บาท</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">ค้างชำระ</Typography>
								<Typography className="font-16" fontWeight={700} color={filteredPeriodSummary.outstanding_amount > 0 ? 'error.main' : 'success.main'}>
									{formatCurrency(filteredPeriodSummary.outstanding_amount)} บาท
								</Typography>
							</Box>
						</Stack>
					</Paper>
				)}

				{/* ── Table ── */}
				{isLoading ? (
					<Box display="flex" justifyContent="center" py={6}>
						<CircularProgress />
					</Box>
				) : filteredPeriods.length === 0 ? (
					<Alert severity="info">ไม่พบรอบวางบิล</Alert>
				) : (
					<Paper variant="outlined" sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ tableLayout: 'auto' }}>
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox" sx={{ pl: 1, width: 40, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }} />
									<TableCell className="font-16" sx={{ fontWeight: 700, py: 1.5, width: 115, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ประเภทรอบ</TableCell>
									<TableCell className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ช่วงวันที่</TableCell>
									<TableCell className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ลูกค้า</TableCell>
								<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ใบกำกับภาษี</TableCell>
								<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ยอดรวม (บาท)</TableCell>
								<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ค้างชำระ (บาท)</TableCell>
								<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>สถานะ</TableCell>
								<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, width: 110, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>เครื่องมือ</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{pagedPeriods.map((period) => (
									<PeriodRow
										key={`${period.start_date}_${period.end_date}`}
										period={period}
										onNavigate={navigateToPeriod}
										canDelete={permission.delete}
                    onDeleteRound={(round) => { if (permission.delete) setDeleteConfirmRound(round); }}
										searchHighlight={searchCustomer.trim() || undefined}
									/>
								))}
							</TableBody>
						</Table>
						<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
							<TablePagination
								component="div"
								count={filteredPeriods.length}
								page={page}
								onPageChange={(_, p) => setPage(p)}
								rowsPerPage={rowsPerPage}
								rowsPerPageOptions={[rowsPerPage]}
								labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count} รอบ`}
								labelRowsPerPage=""
							/>
						</Box>
					</Paper>
				)}
				</Box>
			</ActionPaper>
			)}

			{/* ── Tab 1: Customer View ── */}
			{activeTab === 1 && (
			<ActionPaper>
				<Box sx={{ p: 2 }}>
				{/* ── Filtered Customer Summary Bar ── */}
				{!customersLoading && allCustomers.length > 0 && (
					<Paper variant="outlined" sx={{ px: 2, py: 1, mb: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
						<Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
							<Typography fontSize={11} fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
								สรุปที่แสดง ({filteredCustomerSummary.count} ราย)
							</Typography>
							<Divider orientation="vertical" flexItem />
							<Box>
								<Typography fontSize={10} color="text.secondary">รอบทั้งหมด</Typography>
								<Typography className="font-16" fontWeight={700}>{filteredCustomerSummary.total_rounds.toLocaleString()} รอบ</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">รอเก็บ</Typography>
								<Typography className="font-16" fontWeight={700} color="warning.main">{filteredCustomerSummary.pending_rounds.toLocaleString()} รอบ</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">ใบกำกับภาษี</Typography>
								<Typography className="font-16" fontWeight={700}>{filteredCustomerSummary.invoice_count.toLocaleString()} ใบ</Typography>
							</Box>
							<Divider orientation="vertical" flexItem />
							<Box>
								<Typography fontSize={10} color="text.secondary">ยอดรวม</Typography>
								<Typography className="font-16" fontWeight={700} color="primary.main">{formatCurrency(filteredCustomerSummary.total_amount)} บาท</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">ชำระแล้ว</Typography>
								<Typography className="font-16" fontWeight={700} color="success.main">{formatCurrency(filteredCustomerSummary.paid_amount)} บาท</Typography>
							</Box>
							<Box>
								<Typography fontSize={10} color="text.secondary">ค้างชำระ</Typography>
								<Typography className="font-16" fontWeight={700} color={filteredCustomerSummary.outstanding_amount > 0 ? 'error.main' : 'success.main'}>
									{formatCurrency(filteredCustomerSummary.outstanding_amount)} บาท
								</Typography>
							</Box>
						</Stack>
					</Paper>
				)}

				{/* ── Customer Table ── */}
				{customersLoading ? (
					<Box display="flex" justifyContent="center" py={6}>
						<CircularProgress />
					</Box>
				) : allCustomers.length === 0 ? (
					<Alert severity="info">ไม่พบข้อมูลลูกค้า</Alert>
				) : (
					<Paper variant="outlined" sx={{ overflowX: 'auto' }}>
						<Table size="small" sx={{ tableLayout: 'auto' }}>
							<TableHead>
								<TableRow>
									<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, width: 50, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>#</TableCell>
									<TableCell className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>รหัสลูกค้า</TableCell>
									<TableCell className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ชื่อลูกค้า</TableCell>
									<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>รอบทั้งหมด</TableCell>
									<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>รอเก็บ</TableCell>
									<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ใบกำกับภาษี</TableCell>
									<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ยอดรวม (บาท)</TableCell>
									<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ชำระแล้ว (บาท)</TableCell>
									<TableCell align="right" className="font-16" sx={{ fontWeight: 700, py: 1.5, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>ค้างชำระ (บาท)</TableCell>
									<TableCell align="center" className="font-16" sx={{ fontWeight: 700, py: 1.5, width: 100, bgcolor: '#EFF5F0', borderTop: '1px solid #CACDCF', borderBottom: '1px solid #CACDCF' }}>เครื่องมือ</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{pagedCustomers.map((cust, index) => (
									<TableRow key={cust.customer_code} hover sx={{ cursor: 'pointer' }}>
										<TableCell align="center" className="font-16" sx={{ py: 0.8, color: 'text.secondary' }}>
											{customerPage * customerRowsPerPage + index + 1}
										</TableCell>
										<TableCell sx={{ py: 0.8 }}>
											<Chip label={cust.customer_code} size="small" color="info" variant="outlined" sx={{ fontSize: '0.7rem', fontWeight: 600 }} />
										</TableCell>
										<TableCell className="font-16" sx={{ py: 0.8 }}>
											{cust.customer_name || <Typography color="text.disabled" component="span">—</Typography>}
										</TableCell>
										<TableCell align="center" className="font-16" sx={{ py: 0.8 }}>
											{cust.total_rounds}
										</TableCell>
										<TableCell align="center" sx={{ py: 0.8 }}>
											{cust.pending_rounds > 0 ? (
												<Chip label={cust.pending_rounds} size="small" color="warning" variant="filled" sx={{ fontSize: '0.7rem', fontWeight: 700, minWidth: 28 }} />
											) : (
												<Chip label="0" size="small" color="success" variant="filled" sx={{ fontSize: '0.7rem', fontWeight: 700, minWidth: 28 }} />
											)}
										</TableCell>
										<TableCell align="center" className="font-16" sx={{ py: 0.8 }}>
											{(cust.invoice_count ?? 0).toLocaleString()}
										</TableCell>
										<TableCell align="right" className="font-16" sx={{ py: 0.8 }}>
											{formatCurrency(cust.total_amount)}
										</TableCell>
										<TableCell align="right" className="font-16" sx={{ py: 0.8, color: 'success.main' }}>
											{formatCurrency(cust.paid_amount)}
										</TableCell>
										<TableCell align="right" className="font-16" sx={{ py: 0.8, fontWeight: 700, color: cust.outstanding_amount > 0 ? 'error.main' : 'success.main' }}>
											{formatCurrency(cust.outstanding_amount)}
										</TableCell>
										<TableCell align="center" sx={{ py: 0.8 }}>
											<Button
												size="small"
												variant="outlined"
												color="info"
												onClick={() => {
													const params = new URLSearchParams({
														customer_code: cust.customer_code,
														year: String(yearFilter + 543),
													});
													if (monthFilter > 0) params.append('month', String(monthFilter));
													navigate(`/sales/billing-invoices?${params.toString()}`);
												}}
												sx={{ fontSize: 11, py: 0.2, px: 1 }}
											>
												ดูบิล
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
							<TablePagination
								component="div"
								count={allCustomers.length}
								page={customerPage}
								onPageChange={(_, p) => setCustomerPage(p)}
								rowsPerPage={customerRowsPerPage}
								rowsPerPageOptions={[customerRowsPerPage]}
								labelDisplayedRows={({ from, to, count }) => `${from}–${to} จาก ${count} ราย`}
								labelRowsPerPage=""
							/>
						</Box>
					</Paper>
				)}
				</Box>
			</ActionPaper>
			)}

			{/* ============================================================
			    จัดการรอบบิล — Main Dialog
			    ============================================================ */}
			<Dialog
				open={manageOpen}
				onClose={() => { setManageOpen(false); }}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700 }}>⚙️ จัดการรอบบิล</DialogTitle>
				<DialogContent dividers>
                  <Stack spacing={2}>
                    <Alert severity="info">เพิ่มรอบครึ่งเดือน 1–15 และ 16–สิ้นเดือน ของปีที่เลือก โดยเก็บรอบเดิมและรายการชำระไว้</Alert>
                    <Typography>ปี {yearFilter + 543} · ใช้วันที่ Invoice จัดเข้ารอบ</Typography>
                    <Button variant="contained" disabled={!permission.create || ensureMutation.isLoading}
                      onClick={() => ensureMutation.mutate(yearFilter, { onSuccess: () => setManageOpen(false) })}>
                      {ensureMutation.isLoading ? 'กำลังเพิ่มรอบ...' : 'เพิ่มรอบที่ขาด'}
                    </Button>
                  </Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => { setManageOpen(false); }}>
						ปิด
					</Button>
				</DialogActions>
			</Dialog>

			{/* ============================================================
			    Confirm Delete Round Dialog
			    ============================================================ */}
			<Dialog
				open={!!deleteConfirmRound}
				onClose={() => setDeleteConfirmRound(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>ยืนยันการลบรอบบิล</DialogTitle>
				<DialogContent>
					<Typography>
						คุณต้องการลบรอบบิล &ldquo;<strong>{deleteConfirmRound?.round_label}</strong>&rdquo; ใช่หรือไม่?
					</Typography>
					<Alert severity="warning" sx={{ mt: 1.5, fontSize: 13 }}>
						รอบบิลที่มีการชำระเงินแล้วจะไม่สามารถลบได้
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteConfirmRound(null)}>ยกเลิก</Button>
					<Button
						variant="contained"
						color="error"
						disabled={!permission.delete || deleteMutation.isLoading}
						startIcon={deleteMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
						onClick={() => {
							if (!deleteConfirmRound) return;
							deleteMutation.mutate(deleteConfirmRound.id, {
								onSuccess: () => setDeleteConfirmRound(null),
							});
						}}
					>
						{deleteMutation.isLoading ? 'กำลังลบ...' : 'ยืนยันลบ'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default BillingPage;
